// src/components/crm/QuoteFollowUpStrip.js
// SEAM 6 VERIFIER: qfup-7d3e9a41-strip
//
// Internal "Follow-ups" strip on the operator quote detail page: how many
// attempts have been logged, when the next one is due, the button that opens
// the capture modal — and the attempts themselves, expanded in place.
//
// Stacey Boychuk's seventh dropdown finally has somewhere to live. The point is
// a HISTORY — many attempts per estimate — which a flat spreadsheet could not
// hold. So the count is a BUTTON, not a label: a count that leads nowhere is a
// dead end, and sending someone to the mixed activity feed to pick follow-ups
// out from among sends and stage changes is the same as hiding them.
//
// Rows are rendered by the SHARED QuoteActivityRow, so a follow-up reads
// identically here and on the activity timeline, and the labels can never
// drift apart. Entries come from the SHARED useQuoteActivity subscription
// (passed down by QuoteDetailsView) — this surface opens no listener of its own.
//
// ⚠️ TRUNCATION IS MADE VISIBLE, NOT HIDDEN. The shared subscription reads the
// most recent 50 entries of ALL types, so on a busy estimate an older follow-up
// can fall outside that window. quote.followUpCount is server-authoritative
// (FieldValue.increment, client-denied in firestore.rules), so comparing it
// against the rows actually found tells us exactly when we are showing fewer —
// and we say so. Silent truncation would be the original complaint (a history
// hidden behind a number) arriving later and quieter.
//
// INTERNAL ONLY: never mount this in a portal view, and keep it OUTSIDE the
// document card (which mirrors the customer PDF).
//
// followUpCount and nextFollowUpAt are server-written (logQuoteFollowUp);
// firestore.rules denies client writes to both.
//
// ─── SEAM 7: SNOOZE  (qsnooze-9f4c2e18) ─────────────────────────────────────
// A snooze is "moving the follow-up date plus a suppression flag" (Addendum 4,
// A4.1), so a snoozed quote has exactly ONE date — nextFollowUpAt and
// snoozedUntil hold the same instant. This strip therefore never shows two
// dates: while snoozed it shows the wake-up date AND THE REASON, which is the
// part a salesperson actually needs a week later, and it replaces the
// "Was due" / "Next" pill rather than sitting beside it.
//
// IS-IT-SNOOZED IS DERIVED, never stored (roadmap LOCK 3) — snoozedUntil
// compared against now. So a snooze that has elapsed reads as awake here
// immediately, without waiting for the nightly sweep that writes its audit row.
import React, { useState } from 'react';
import { PhoneCall, CalendarClock, ChevronDown, ChevronUp, BellOff } from 'lucide-react';
import { toJsDate } from '../../utils/formatters';
import { can, PERMISSIONS } from '../../utils/permissions';
// qattn-8c4e1f92-strip-unify — the overdue test moved into quoteSchema.js so
// this strip, the calendar and the Seam 8 rule share ONE implementation. The
// behaviour change is the timezone: it was the VIEWER's midnight, it is now the
// org's, which is what "due today" has always meant on the estimate itself.
import { QUOTE_ACTIVITY_TYPES as T, resolveDocTerminology, quoteFollowUpOverdueDays } from '../../utils/quoteSchema';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import QuoteActivityRow from './QuoteActivityRow';
import LogFollowUpModal from './LogFollowUpModal';
import SnoozeQuoteModal from './SnoozeQuoteModal';
import useQuoteSnooze from '../../hooks/useQuoteSnooze'; // s5portal-8a3d6f21

const QuoteFollowUpStrip = ({
    orgId,
    quote,
    vocabularies,
    currentUserProfile,
    docWord = 'Estimate',
    entries = [],
    loadError = false,
    companySettings = null, // qattn-8c4e1f92-strip-unify — for the ORG timezone
}) => {
    const [open, setOpen] = useState(false);
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const [waking, setWaking] = useState(false);
    const [wakeError, setWakeError] = useState('');
    const [expanded, setExpanded] = useState(false);
    // s5portal-8a3d6f21 — the reason now lives in the staff-only quoteSnoozes doc (it used to be
    // quote.snoozeReason, which the customer portal could read). Called before the early return
    // below (rules of hooks); only subscribes while the quote carries a snooze date.
    const { reason: snoozeReason } = useQuoteSnooze(orgId, quote?.id, !!quote?.snoozedUntil);

    if (!orgId || !quote?.id) return null;

    const count = Number.isFinite(quote.followUpCount) ? quote.followUpCount : 0;
    const nextAt = toJsDate(quote.nextFollowUpAt);
    const canLog = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS);
    const { word: rowDocWord } = resolveDocTerminology(quote?.docType);

    const rows = entries.filter((e) => e.type === T.FOLLOW_UP);
    // The count is authoritative; the rows are a window onto it.
    const missing = Math.max(0, count - rows.length);

    const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    // qsnooze-9f4c2e18 — followUpHasTime is what separates a real 12:00 from
    // Seam 6's date-only noon proxy. Without it every date-only follow-up would
    // read as "at 12:00 PM", which is a promise nobody made.
    const fmtWhen = (d) => (quote.followUpHasTime
        ? `${fmt(d)}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
        : fmt(d));
    // DERIVED, never stored (LOCK 3).
    const snoozedUntil = toJsDate(quote.snoozedUntil);
    const snoozed = snoozedUntil && snoozedUntil > new Date();
    // qattn-8c4e1f92-strip-unify — ONE overdue predicate, shared with the
    // calendar and the Seam 8 attention rule. It carries the snooze suppression
    // itself, so the `!snoozed &&` that used to be here is not lost, just moved.
    const overdue = quoteFollowUpOverdueDays(quote, { timeZone: companySettings?.timezone }) !== null;

    const handleWakeNow = async () => {
        if (waking) return;                       // single-fire guard
        setWaking(true);
        setWakeError('');
        try {
            await httpsCallable(functions, 'setQuoteSnooze')({ quoteId: quote.id, clear: true });
            // The live quote listener clears the pill; nothing to set here.
        } catch (err) {
            console.error('QuoteFollowUpStrip: wake now failed', err);
            setWakeError(err?.message || 'Could not wake this up. Please try again.');
        } finally {
            setWaking(false);
        }
    };

    return (
        <>
            <div className="max-w-4xl mx-auto mb-4 bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <PhoneCall size={16} /> Follow-ups
                    </span>

                    {count === 0 ? (
                        <span className="text-sm text-gray-800">None logged yet</span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setExpanded((prev) => !prev)}
                            className="flex items-center gap-1 text-sm text-gray-800 rounded px-1.5 py-0.5 -mx-1 hover:bg-gray-100"
                            title={expanded ? 'Hide the attempts' : 'Show the attempts'}
                        >
                            <span className="font-medium underline decoration-dotted underline-offset-2">
                                {count} logged
                            </span>
                            {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                        </button>
                    )}

                    {snoozed ? (
                        <>
                            <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-slate-100 text-slate-600 border border-slate-200 rounded-full">
                                <BellOff size={12} />
                                Snoozed until {fmtWhen(snoozedUntil)}
                                {snoozeReason ? ` — ${snoozeReason}` : ''}
                            </span>
                            {/* qsnooze-9f4c2e18-explain — founder test, 2026-09-27: the pill
                                alone did not say what a snooze DOES, and a tooltip is not an
                                explanation because it is invisible until hovered (and absent
                                on touch). Plain text in the same wrapping row, so no layout
                                changes. */}
                            <span className="text-xs text-gray-500">
                                Automatic reminders paused until {fmtWhen(snoozedUntil)}.
                            </span>
                        </>
                    ) : nextAt && (
                        <span className={`flex items-center gap-1 px-2 py-0.5 text-xs border rounded-full ${
                            overdue
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                            <CalendarClock size={12} />
                            {overdue ? 'Was due' : 'Next'} {fmtWhen(nextAt)}
                        </span>
                    )}

                    {canLog && (
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-[#5F4EC9] rounded-lg hover:bg-[#4f40a8]"
                        >
                            Log follow-up
                        </button>
                    )}

                    {canLog && (snoozed ? (
                        <button
                            type="button"
                            onClick={handleWakeNow}
                            disabled={waking}
                            title="Stop snoozing and let reminders resume"
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        >
                            {waking ? 'Waking…' : 'Wake now'}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setSnoozeOpen(true)}
                            title="Stop chasing this until a date you choose"
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                            Snooze
                        </button>
                    ))}

                    <span className="ml-auto text-xs text-gray-400">Internal: customers never see this</span>
                </div>

                {wakeError && (
                    <p className="px-4 pb-3 -mt-1 text-sm text-red-600">{wakeError}</p>
                )}

                {expanded && count > 0 && (
                    <div className="border-t border-gray-100 px-4 py-3">
                        {loadError ? (
                            <p className="text-sm text-red-600 text-center py-3">
                                Couldn&apos;t load the follow-ups. Try reopening this {docWord.toLowerCase()}.
                            </p>
                        ) : rows.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-3">
                                {count === 1 ? 'The attempt is' : 'The attempts are'} older than the most recent
                                50 activity entries on this {docWord.toLowerCase()} — open Activity below to find
                                {count === 1 ? ' it' : ' them'}.
                            </p>
                        ) : (
                            <>
                                <ul className="divide-y divide-gray-100">
                                    {rows.map((entry) => (
                                        <QuoteActivityRow
                                            key={entry.id}
                                            entry={entry}
                                            docWord={rowDocWord}
                                        />
                                    ))}
                                </ul>
                                {missing > 0 && (
                                    <p className="mt-3 text-xs text-gray-500">
                                        Showing the {rows.length} most recent of {count}. The other {missing} fall
                                        outside the most recent 50 activity entries on this {docWord.toLowerCase()}.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <LogFollowUpModal
                isOpen={open}
                onClose={() => setOpen(false)}
                quote={quote}
                vocabularies={vocabularies}
                docWord={docWord}
            />

            <SnoozeQuoteModal
                isOpen={snoozeOpen}
                onClose={() => setSnoozeOpen(false)}
                quote={quote}
                docWord={docWord}
            />
        </>
    );
};

export default QuoteFollowUpStrip;
